import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { isNativeApp } from "./capacitor";

export type BrandTheme = "orange" | "teal" | "green" | "blue" | "purple" | "red";
export type BrandId = "centretable" | "shiftcanvas";

interface BrandConfig {
  name: string;
  logo: string;
  theme: BrandTheme;
  brand: BrandId;
  showBundleNav: boolean;
}

interface BrandContextValue {
  config: BrandConfig | null;
  isLoading: boolean;
  isCentretable: boolean;
  isShiftCanvas: boolean;
  isNativeMobile: boolean;
  setBrandOverride: (brand: BrandId) => void;
}

const defaultConfig: BrandConfig = {
  name: "Centretable",
  logo: "/logos/centretable.svg",
  theme: "orange",
  brand: "centretable",
  showBundleNav: true,
};

const shiftcanvasConfig: BrandConfig = {
  name: "ShiftCanvas",
  logo: "/images/shiftcanvas-logo-text.png",
  theme: "teal",
  brand: "shiftcanvas",
  showBundleNav: false,
};

// Detect brand synchronously at module load to prevent loading flash
function detectInitialBrand(): BrandConfig {
  if (typeof window === 'undefined') {
    return defaultConfig;
  }
  const host = window.location.hostname;
  if (isNativeApp() || host.includes("shiftcanvas")) {
    return shiftcanvasConfig;
  }
  return defaultConfig;
}

const initialConfig = detectInitialBrand();

const BrandContext = createContext<BrandContextValue>({
  config: initialConfig,
  isLoading: false,
  isCentretable: initialConfig.brand === "centretable",
  isShiftCanvas: initialConfig.brand === "shiftcanvas",
  isNativeMobile: isNativeApp(),
  setBrandOverride: () => {},
});

export function BrandProvider({ children }: { children: ReactNode }) {
  // Initialize with the detected brand synchronously - no loading state needed
  const [config, setConfig] = useState<BrandConfig>(initialConfig);

  const applyBrand = (brand: BrandId) => {
    if (brand === "shiftcanvas") {
      setConfig(shiftcanvasConfig);
      document.documentElement.setAttribute("data-brand", "shiftcanvas");
    } else {
      setConfig(defaultConfig);
      document.documentElement.setAttribute("data-brand", "centretable");
    }
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-brand", config.brand);
    const metaDesc = document.querySelector('meta[name="description"]');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (config.brand === "shiftcanvas") {
      document.title = "ShiftCanvas - Simple Rostering for Small Teams";
      if (metaDesc) metaDesc.setAttribute("content", "Simple rostering and scheduling for small teams. Free for up to 10 staff. Manage shifts, clock in/out, messaging and more.");
      if (ogTitle) ogTitle.setAttribute("content", "ShiftCanvas - Simple Rostering for Small Teams");
      if (ogDesc) ogDesc.setAttribute("content", "Simple rostering and scheduling for small teams. Free for up to 10 staff.");
    } else {
      document.title = "Centretable POS - Create Your Perfect Point of Sale";
      if (metaDesc) metaDesc.setAttribute("content", "Build your perfect point of sale system with Centretable. Up to 10 products free, forever. Scalable plans for cafes, retail, restaurants and more.");
      if (ogTitle) ogTitle.setAttribute("content", "Centretable POS - Create Your Perfect Point of Sale");
      if (ogDesc) ogDesc.setAttribute("content", "Build your perfect point of sale system with Centretable. Up to 10 products free, forever.");
    }
  }, [config.brand]);

  const setBrandOverride = (brand: BrandId) => {
    applyBrand(brand);
  };

  const value: BrandContextValue = {
    config,
    isLoading: false, // Brand detection is now synchronous
    isCentretable: config.brand === "centretable",
    isShiftCanvas: config.brand === "shiftcanvas",
    isNativeMobile: isNativeApp(),
    setBrandOverride,
  };

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand() {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error("useBrand must be used within a BrandProvider");
  }
  return context;
}
